import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { supabase, uploadFile, BUCKETS } from '@/lib/supabase'
import { useAuthStore } from '@/store/authStore'
import { format } from 'date-fns'
import type { Entry, Capsule, VaultDocument, VaultPermission, LegacyContact, InformPost } from '@/types/database'

// ─── ENTRIES ─────────────────────────────────────────────
export function useEntries(year?: number) {
  const user = useAuthStore(s => s.user)
  return useInfiniteQuery({
    queryKey: ['entries', user?.id, year],
    queryFn: async ({ pageParam = 0 }) => {
      let q = supabase
        .from('entries')
        .select('*')
        .eq('user_id', user!.id)
        .order('recorded_at', { ascending: false })
        .range(pageParam * 20, pageParam * 20 + 19)
      if (year) {
        q = q
          .gte('recorded_at', `${year}-01-01`)
          .lte('recorded_at', `${year}-12-31`)
      }
      const { data, error } = await q
      if (error) throw error
      return data as Entry[]
    },
    getNextPageParam: (last, all) => last.length === 20 ? all.length : undefined,
    initialPageParam: 0,
    enabled: !!user,
  })
}

export function useEntriesOnDate(month: number, day: number) {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['entries-on-date', user?.id, month, day],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_entries_on_date', {
        target_month: month,
        target_day: day,
        p_user_id: user!.id,
      })
      if (error) throw error
      return data as Entry[]
    },
    enabled: !!user,
  })
}

export function useCreateEntry() {
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  return useMutation({
    mutationFn: async (payload: {
      title: string
      body?: string
      entryType: Entry['entry_type']
      privacy: Entry['privacy']
      tags: string[]
      // Pass either pre-uploaded URLs (new approach) OR raw files (old approach)
      mediaUrls?: string[]
      files?: File[]
      locationName?: string
      isMilestone?: boolean
      milestoneIcon?: string
      recordedAt?: string
    }) => {
      let finalUrls: string[] = payload.mediaUrls ?? []

      // Fallback: upload raw files if mediaUrls not provided
      if (!finalUrls.length && payload.files?.length) {
        finalUrls = await Promise.all(
          payload.files.map(f => {
            const path = `${user!.id}/${Date.now()}-${f.name}`
            return uploadFile(BUCKETS.MEMORIES, path, f)
          })
        )
      }

      const { data, error } = await supabase.from('entries').insert({
        user_id:       user!.id,
        title:         payload.title,
        body:          payload.body ?? null,
        entry_type:    payload.entryType,
        privacy:       payload.privacy,
        tags:          payload.tags,
        media_urls:    finalUrls,
        location_name: payload.locationName ?? null,
        is_milestone:  payload.isMilestone ?? false,
        milestone_icon: payload.milestoneIcon ?? null,
        recorded_at:   payload.recordedAt ?? new Date().toISOString(),
        recorded_date: payload.recordedAt
          ? new Date(payload.recordedAt).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
      }).select().single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entries', user?.id] })
    },
  })
}

export function useDeleteEntry() {
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('entries').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entries', user?.id] }),
  })
}

// ─── CAPSULES ─────────────────────────────────────────────
export function useCapsules() {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['capsules', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('capsules')
        .select('*')
        .eq('user_id', user!.id)
        .order('unlock_date', { ascending: true })
      if (error) throw error
      return data as Capsule[]
    },
    enabled: !!user,
  })
}

export function useCreateCapsule() {
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  return useMutation({
    mutationFn: async (payload: Omit<Capsule, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('capsules')
        .insert({ ...payload, user_id: user!.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['capsules', user?.id] }),
  })
}

// ─── VAULT ─────────────────────────────────────────────
export function useVaultDocuments() {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['vault', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vault_documents')
        .select('*, vault_permissions(*)')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as (VaultDocument & { vault_permissions: VaultPermission[] })[]
    },
    enabled: !!user,
  })
}

export function useAddVaultDocument() {
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  return useMutation({
    mutationFn: async (payload: { title: string; docType: VaultDocument['doc_type']; file?: File; notes?: string }) => {
      let fileUrl: string | null = null
      let fileSizeBytes: number | null = null
      if (payload.file) {
        const path = `${user!.id}/${Date.now()}-${payload.file.name}`
        fileUrl = await uploadFile(BUCKETS.DOCUMENTS, path, payload.file)
        fileSizeBytes = payload.file.size
      }
      const { data, error } = await supabase.from('vault_documents').insert({
        user_id: user!.id,
        title: payload.title,
        doc_type: payload.docType,
        file_url: fileUrl,
        file_size_bytes: fileSizeBytes,
        notes: payload.notes ?? null,
        is_sealed: false,
      }).select().single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault', user?.id] }),
  })
}

// ─── LEGACY CONTACTS ─────────────────────────────────────
export function useLegacyContacts() {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['legacy-contacts', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legacy_contacts')
        .select('*')
        .eq('user_id', user!.id)
      if (error) throw error
      return data as LegacyContact[]
    },
    enabled: !!user,
  })
}

// ─── DISCOVER ─────────────────────────────────────────────
export function usePublicProfiles(limit = 20) {
  return useQuery({
    queryKey: ['public-profiles', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, full_name, avatar_url, location, bio')
        .limit(limit)
      if (error) throw error
      return data
    },
  })
}

export function useFollowUser() {
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  return useMutation({
    mutationFn: async (followingId: string) => {
      const { error } = await supabase
        .from('followers')
        .insert({ follower_id: user!.id, following_id: followingId })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['public-profiles'] }),
  })
}

// ─── INFORM ─────────────────────────────────────────────
export function useInformPosts(category?: string, location?: string) {
  return useQuery({
    queryKey: ['inform-posts', category, location],
    queryFn: async () => {
      let q = supabase
        .from('inform_posts')
        .select('*, profiles(username, avatar_url, full_name)')
        .order('published_at', { ascending: false })
        .limit(30)
      if (category) q = q.eq('category', category)
      if (location) q = q.ilike('location_name', `%${location}%`)
      const { data, error } = await q
      if (error) throw error
      return data
    },
  })
}

export function useCreateInformPost() {
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  return useMutation({
    mutationFn: async (payload: Omit<InformPost, 'id' | 'author_id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('inform_posts')
        .insert({ ...payload, author_id: user!.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inform-posts'] }),
  })
}

// ─── PROFILE ─────────────────────────────────────────────
export function useProfile(userId?: string) {
  const currentUser = useAuthStore(s => s.user)
  const id = userId ?? currentUser?.id
  return useQuery({
    queryKey: ['profile', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id!)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })
}

// ─── ENTRY STATS ─────────────────────────────────────────
export function useEntryStats() {
  const user = useAuthStore(s => s.user)
  return useQuery({
    queryKey: ['entry-stats', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('entries')
        .select('recorded_at, is_milestone, tags')
        .eq('user_id', user!.id)
      if (error) throw error
      const years = [...new Set(data.map(e => new Date(e.recorded_at).getFullYear()))].sort()
      const milestones = data.filter(e => e.is_milestone).length
      return { total: data.length, years, milestones, entries: data }
    },
    enabled: !!user,
  })
}
